import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { CombinedContext } from "../../context/combinedContext";
import "./footer.css";
import translations from "../../translations/translations";
import logo from "../../images/logoTransparent.png";
import { FaInstagram, FaFacebookF, FaTwitter, FaApple, FaGooglePlay } from "react-icons/fa";
import { Globe } from "lucide-react";
import ReactCountryFlag from "react-country-flag";

const toTop = () => window.scrollTo({ top: 0 });

/* Same deal as the stores: fill in the profile URL and the icon becomes a link. */
const SOCIALS = [
	{ id: "instagram", label: "Instagram", url: null, Icon: FaInstagram },
	{ id: "facebook", label: "Facebook", url: null, Icon: FaFacebookF },
	{ id: "twitter", label: "Twitter", url: null, Icon: FaTwitter },
];

/* Neither app has shipped yet, so the badges render as static chips.
   Drop the store URL in here and it becomes a real link — nothing else changes. */
const STORES = [
	{ id: "appStore", url: null, Icon: FaApple },
	{ id: "googlePlay", url: null, Icon: FaGooglePlay },
];

const Footer = () => {
	const { language, setLanguage, userData } = useContext(CombinedContext);

	const isEN = language === "en";
	const role = userData?.user?.role;
	const t = translations[language].footer;

	/* Owners, admins and staff already run a shop — the pitch link is for everyone else. */
	const showListShop = !role || role === "customer";

	return (
		<footer className="sf-footer">
			<div className="sf-footer-grid-tex" aria-hidden="true"></div>
			<div className="sf-footer-glow" aria-hidden="true"></div>

			<div className="sf-footer-inner">
				{/* ======= MAIN COLUMNS ======= */}
				<div className="sf-footer-cols">
					{/* BRAND */}
					<div className="sf-footer-brandcol">
						<Link to="/" onClick={toTop} className="sf-footer-logolink" aria-label="ReservaSinFila">
							<img src={logo} alt="ReservaSinFila" className="sf-footer-logo" />
						</Link>

						{/* <p className="sf-footer-tagline">{t.tagline}</p> */}

						<span className="sf-footer-flagpill">
							<ReactCountryFlag
								countryCode="DO"
								svg
								style={{ width: "1.1em", height: "1.1em" }}
								aria-label="Dominican Republic"
							/>
							{t.madeInDR}
						</span>

						<div className="sf-footer-socials" aria-label={t.socialsLabel}>
							{SOCIALS.map(({ id, label, url, Icon }) =>
								url ? (
									<a key={id} href={url} aria-label={label} target="_blank" rel="noreferrer">
										<Icon />
									</a>
								) : (
									<span key={id} className="is-pending" aria-hidden="true">
										<Icon />
									</span>
								)
							)}
						</div>
					</div>

					{/* COMPANY */}
					<nav className="sf-footer-navcol" aria-label={t.columns.company}>
						<h3>{t.columns.company}</h3>
						<ul>
							<li>
								<Link to="/about" onClick={toTop}>{t.links.about}</Link>
							</li>
							<li>
								<Link to="/contact" onClick={toTop}>{t.links.contact}</Link>
							</li>
							{showListShop && (
								<li>
									<Link to="/businesslanding" onClick={toTop}>
										{translations[language].navbar.auth.listShop}
									</Link>
								</li>
							)}
						</ul>
					</nav>

					{/* LEGAL */}
					<nav className="sf-footer-navcol" aria-label={t.columns.legal}>
						<h3>{t.columns.legal}</h3>
						<ul>
							<li>
								<Link to="/privacy" onClick={toTop}>{t.links.privacy}</Link>
							</li>
							<li>
								<Link to="/terms" onClick={toTop}>{t.links.terms}</Link>
							</li>
							<li>
								<Link to="/refund" onClick={toTop}>{t.links.refundPolicy}</Link>
							</li>
						</ul>
					</nav>

					{/* APPS */}
					<div className="sf-footer-appcol">
						<h3>{t.columns.getApp}</h3>
						<p className="sf-footer-appnote">{t.apps.note}</p>

						<div className="sf-footer-apps">
							{STORES.map(({ id, url, Icon }) => {
								const badge = (
									<>
										<Icon className="sf-footer-store-icon" />
										<span className="sf-footer-store-text">
											<small>{t.apps[`${id}Small`]}</small>
											<strong>{t.apps[`${id}Name`]}</strong>
										</span>
										{!url && <span className="sf-footer-store-soon">{t.apps.soon}</span>}
									</>
								);

								return url ? (
									<a
										key={id}
										href={url}
										className="sf-footer-store"
										aria-label={t.apps[`${id}Aria`]}
									>
										{badge}
									</a>
								) : (
									<span key={id} className="sf-footer-store is-pending">
										{badge}
									</span>
								);
							})}
						</div>
					</div>
				</div>

				{/* ======= BOTTOM BAR ======= */}
				<div className="sf-footer-bottom">
					<span className="sf-footer-copy">
						© {new Date().getFullYear()} ReservaSinFila. {t.rights}
					</span>

					<button
						type="button"
						className="sf-footer-lang"
						onClick={() => setLanguage(isEN ? "es" : "en")}
					>
						<Globe size={15} strokeWidth={1.9} />
						{t.languageSwitch}
					</button>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
